<template>
    <div class="chat-window container relative h-full bg-purple-900">

        <alert ref="alert"></alert>

        <float-button class="m-10 bg-blue-500" @click="syncHistory">
            <icon icon="history"></icon>
        </float-button>

        <div v-if="loadingMessages && !messages.length" class="flex justify-center items-center h-full">
            <div class="loader"></div>
        </div>

        <div v-if="!loadingMessages && !messages.length" class="flex justify-center items-center h-full">
            {{ $t('no_old_messages_found') }}
        </div>

        <div ref="window"
             class="messages flex flex-col items-end content-end h-full overflow-y-auto p-5 overflow-x-hidden md:p-10"
             @scroll="onScroll" v-if="messages.length">
            <div class="p-10 w-full text-center border-b border-white opacity-25 mb-8" v-if="currentPage === lastPage">
                {{ $t('no_old_messages_found') }}
            </div>

            <div class="message-container w-full" v-for="(m, k) in messages" :key="k">
                <div class="message flex"
                     :class="{'from-me': m.from_me}">

                    <img class="w-10 h-10 rounded-full mr-2"
                         @error="imageError"
                         v-if="!m.from_me"
                         :src="(m.contact && m.contact.image_url) || '/images/unknown.jpg'">

                    <div class="w-full">
                        <div class="text-sm">
                            <div v-if="!m.from_me">
                                <span v-if="m.contact">
                                    {{ m.contact.name ? m.contact.name : m.contact.wa_number }}
                                </span>

                                <span v-else-if="!m.contact">
                                    {{ m.from_id }}
                                </span>

                                <span class="text-sm lowercase pl-1">
                                    {{ $t('was_sent') }}:
                                </span>
                            </div>
                        </div>

                        <component
                            v-if="componentsMap[m.content_type]"
                            v-bind:is="componentsMap[m.content_type]" :message="m"
                            @redraw="redrawn(k, $event)"
                            :instance-id="id"></component>

                        <component v-else v-bind:is="componentsMap['UNKNOWN']"
                                   :instance-id="id"
                                   @redraw="redrawn(k, $event)"
                                   :message="m"></component>

                        <p v-if="m.content_type !== 'STUB'" class="text-sm" v-bind:class="{'text-right': m.from_me}">
                            {{ $filters.dt(m.received_at) }}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style>
.chat-window.container {
    @apply overflow-x-hidden;
}

.chat-window .messages {
    @apply h-full flex items-start flex-col;
}

.chat-window .message-container {
    @apply block;
}

.chat-window .message {
    @apply my-2 w-full;

    -webkit-touch-callout: default;
    -webkit-user-select: auto;
    -khtml-user-select: auto;
    -moz-user-select: auto;
    -ms-user-select: auto;
    user-select: auto;

    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
}

.chat-window .m-content {
    @apply p-4 bg-white text-purple-900 rounded w-full;
}

.chat-window .message.from-me {
    @apply self-end text-right;
}

.chat-window .imageContainer.notLoaded {
    @apply bg-purple-600;
}
</style>

<script>
import ConversationComponent from "./messages/ConversationComponent.vue";
import ImageComponent from "./messages/ImageComponent.vue";
import ExtendedMessageComponent from "./messages/ExtendedMessageComponent.vue";
import UnknownComponent from "./messages/UnknownComponent.vue";
import VideoComponent from "./messages/VideoComponent.vue";
import StubComponent from "./messages/StubComponent.vue";
import AudioComponent from "./messages/AudioComponent.vue";
import DocumentComponent from "./messages/DocumentComponent.vue";
import StickerComponent from "./messages/StickerComponent.vue";
import ContactComponent from "./messages/ContactComponent.vue";
import LocationComponent from "./messages/LocationComponent.vue";
import ProductComponentVue from './messages/ProductComponent.vue';
import ContactArrayMessageComponent from './messages/ContactArrayMessageComponent.vue';
import {Zapin} from "@/services/Api";
import {shallowRef} from "vue";

export default {
    props: {
        id: String,
        chat: String
    },

    mounted() {
        this.loadMessages();
    },

    data() {
        return {
            messages: [],
            componentsMap: {
                'CONVERSATION': shallowRef(ConversationComponent),
                'TEMPLATE_BUTTON_REPLY_MESSAGE': shallowRef(ExtendedMessageComponent),
                'HYDRATED_CONTENT_TEXT': shallowRef(ConversationComponent),
                'IMAGE': shallowRef(ImageComponent),
                'STICKER': shallowRef(StickerComponent),
                'EXTENDED_MESSAGE': shallowRef(ExtendedMessageComponent),
                'PRODUCT_MESSAGE': shallowRef(ProductComponentVue),
                'VIDEO': shallowRef(VideoComponent),
                'DOCUMENT': shallowRef(DocumentComponent),
                'AUDIO': shallowRef(AudioComponent),
                'STUB': shallowRef(StubComponent),
                'CONTACT': shallowRef(ContactComponent),
                'CONTACTS_ARRAY_MESSAGE': shallowRef(ContactArrayMessageComponent),
                'LOCATION': shallowRef(LocationComponent),
                'UNKNOWN': shallowRef(UnknownComponent)
            },
            resourceUrl: null,
            currentPage: 0,
            lastPage: 1,
            loadingMessages: false,
            currentHeight: 0,
            scrollPosition: 0,
            scrollHeight: 0
        }
    },

    methods: {
        onScroll(e) {
            this.scrollPosition = e.target.scrollTop;
            this.scrollHeight = e.target.scrollHeight;

            if (this.scrollPosition < 700 && !this.loadingMessages) {
                this.loadMessages();
            }
        },

        syncHistory() {
            this.$router.push({
                name: 'SyncConversation',
                params: {
                    id: this.id,
                    chat: this.chat
                }
            });
        },

        goBottom() {
            let el = this.$refs.window;

            if(el) {
                this.scrollTo(el.scrollHeight - el.clientHeight);
            }
        },

        scrollTo(y) {
            this.$refs.window.scrollTo(0, y);
        },

        imageError(e) {
            e.target.src = '/images/unknown.jpg';
        },

        alert(message) {
            this.$refs.alert.show(message);
        },

        redrawn(key, message) {
            this.messages[key].content = message.content;
            this.messages[key].content_type = message.content_type;

            this.$forceUpdate();
        },

        loadMessages() {
            if (this.currentPage < this.lastPage) {
                this.currentPage++;
                this.loadingMessages = true;

                Zapin
                    .chats()
                    .messages(this.id, this.chat, this.currentPage)
                    .then(res => {
                        let currentHeight = this.scrollHeight;

                        this.messages = res.data.concat(this.messages);
                        this.lastPage = res.meta.last_page;

                        if (this.currentPage === 1) {
                            setTimeout(() => {
                                this.goBottom();
                            }, 100);
                        } else {
                            setTimeout(() => {
                                this.scrollTo(this.$refs.window.scrollHeight - currentHeight);
                            }, 100);
                        }
                    })
                    .finally(() => this.loadingMessages = false)
            }
        }
    }

}
</script>
